export const CLIENT_BILLING_ITEM_NAMES = Object.freeze({
  workspace: 'ClientBillingWorkspace',
  profile: 'ClientBillingProfile',
  ledger: 'ClientBillingLedger',
  manualRequest: 'ClientBillingManualRowRequest',
  controlRequest: 'ClientBillingControlRequest',
});

export const CLIENT_BILLING_OPERATIONS = Object.freeze({
  load: 'LoadClientBilling',
  saveConfiguration: 'SaveClientBillingConfiguration',
  calculate: 'CalculateClientBillingLedger',
  addManualRow: 'AddClientBillingManualRow',
  setControl: 'SetClientBillingControl',
  loadSources: 'LoadClientBillingSources',
  aiQuestion: 'AIQuestionClientBilling',
});

const BILLING_AI_MANAGE_SURFACES = Object.freeze([
  'configuration',
  'manual',
  'control',
]);

const BILLING_AI_CONTROL_ACTIONS = Object.freeze([
  'Review',
  'Approve',
  'ClosePeriod',
  'Hold',
  'ReleaseHold',
  'Lock',
  'Unlock',
]);

export const normalizeBillingAIManageProposal = (value) => {
  let candidate = value;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  candidate = candidate?.output ?? candidate?.manage ?? candidate;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }
  const surface = String(candidate.surface || '').trim().toLowerCase();
  if (!BILLING_AI_MANAGE_SURFACES.includes(surface)) return null;
  const action = String(candidate.action || '').trim();
  const ledgerRowId = String(candidate.ledgerRowId || '').trim();
  if (surface !== 'control' && (action || ledgerRowId)) return null;
  if (surface === 'control' && !BILLING_AI_CONTROL_ACTIONS.includes(action)) {
    return null;
  }
  if (
    surface === 'control'
    && ['Hold', 'ReleaseHold', 'Lock', 'Unlock'].includes(action)
    && !ledgerRowId
  ) {
    return null;
  }
  return {
    surface,
    action,
    ledgerRowId,
  };
};

export const CLIENT_BILLING_PROFILE_FIELDS = Object.freeze([
  'DisplayName',
  'BillingPeriodType',
  'BillingPeriodAnchorValue',
  'TransactionMode',
  'DefaultCostPerTransaction',
  'IncludedUnits',
  'WarningThresholdUnits',
  'HardLimitUnits',
  'MaxBillableUnits',
  'Currency',
  'EffectiveStartDateUtc',
  'EffectiveEndDateUtc',
  'BillingEnabled',
  'CalculationCadence',
  'CalculationCadenceAnchorValue',
  'SkuConfigurations',
  'RateRules',
]);

export const selectBillingFields = (metadata, allowedFields) => {
  const allowed = new Set(
    allowedFields.map((field) => field.toLowerCase()),
  );
  return (metadata?.Fields ?? metadata?.fields ?? []).filter((field) => (
    allowed.has(
      String(field?.FieldName ?? field?.fieldName ?? '').toLowerCase(),
    )
  ));
};

export const createManualBillingFields = (metadata, activeOffers) => (
  (metadata?.Fields ?? metadata?.fields ?? []).map((field) => {
    if (String(field?.FieldName ?? '').toLowerCase() !== 'clientproductofferid') {
      return field;
    }
    return {
      ...field,
      FieldType: 'SelectList',
      DataJson: JSON.stringify((activeOffers || []).map((offer) => ({
        Text: `${offer.ProductOfferSku} — ${offer.DisplayName}`,
        Value: offer.ClientProductOfferId,
      }))),
      DataOperation: null,
      DataOperationApp: null,
      DataOperationItem: null,
    };
  })
);

export const formatBillingCurrency = (
  value,
  currency = 'USD',
) => {
  const amount = Number(value ?? 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'MULTI' ? 'USD' : currency,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    return `${currency} ${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
};

export const formatBillingDate = (value, fallback = 'Not calculated') => {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? fallback
    : date.toLocaleString();
};

export const formatBillingPeriodDate = (value, fallback = 'Period date') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

export const billingReviewStatus = (value) => ({
  1: 'Open',
  2: 'Reviewed',
  3: 'Approved',
  4: 'Closed',
}[Number(value)] || 'Open');

export const createBillingControlDraft = (
  action,
  ledgerRowId = '',
) => ({
  Action: action,
  LedgerRowId: ledgerRowId,
  Reason: '',
  Confirmed: false,
});

export const createManualBillingDraft = (activeOffers = []) => ({
  ClientProductOfferId:
    activeOffers.length === 1
      ? activeOffers[0].ClientProductOfferId
      : '',
  Category: 6,
  Quantity: 1,
  UnitPrice: 0,
  FeeAmount: 0,
  CreditAmount: 0,
  AdjustmentAmount: 0,
  Reason: '',
  ManualEntryKey: globalThis.crypto?.randomUUID?.()
    ?? `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`,
});

export const shiftBillingPeriod = (
  periodStart,
  periodEnd,
  direction,
  weekly = false,
) => {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  if (weekly) {
    start.setUTCDate(start.getUTCDate() + (7 * direction));
    end.setUTCDate(end.getUTCDate() + (7 * direction));
  } else {
    start.setUTCMonth(start.getUTCMonth() + direction);
    end.setUTCMonth(end.getUTCMonth() + direction);
  }
  return {
    PeriodStartUtc: start.toISOString(),
    PeriodEndUtc: end.toISOString(),
  };
};
